import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import ReactionChainsStrip from "../components/ReactionChainsStrip";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1767801829041-f5243a34525d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1621826269707-5b27afe47c79?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1734448824631-458e1b9a8b5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];

export default function Explore() {
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const loadExplore = useCallback(async () => {
    try {
      const { data } = await api.get("/posts/explore");
      setPosts(data);
    } catch (err) {
      console.error("explore load failed", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadExplore(); }, [loadExplore]);

  useEffect(() => {
    if (!q.trim()) { setUsers([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/search`, { params: { q } });
        setUsers(data);
      } catch (err) {
        console.error("user search failed", err);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-[100dvh] bg-ink pb-32">
      <header className="sticky top-0 z-30 crystal-glass px-5 pt-6 pb-4">
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Discover</p>
        <div className="flex items-center justify-between">
          <h1 className="text-cream text-3xl font-black tracking-tighter">Explore the wave</h1>
          <Link to="/trending" data-testid="explore-to-trending" className="px-3 py-1.5 rounded-full bg-papaya/20 border border-papaya/40 text-papaya text-[10px] font-black tracking-[0.2em] uppercase hover:bg-papaya/30 transition-colors">
            # Trending
          </Link>
        </div>
        <div className="mt-4 flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5">
          <MagnifyingGlass size={18} className="text-cream/50" />
          <input
            data-testid="explore-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search creators, vibes, hashtags…"
            className="flex-1 bg-transparent text-cream placeholder:text-cream/40 focus:outline-none text-sm"
          />
        </div>
      </header>

      {users.length > 0 && (
        <section className="px-5 mt-4">
          <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Creators</p>
          <div className="space-y-2">
            {users.map((u) => (
              <Link
                key={u.id}
                to={`/u/${u.username}`}
                data-testid={`search-user-${u.username}`}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black flex-shrink-0">
                  {u.avatar_path ? <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" /> : (u.display_name || u.username)[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-cream font-bold">{u.display_name}</p>
                  <p className="text-cream/50 text-xs">@{u.username}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ReactionChainsStrip />

      <section className="px-3 mt-5">
        <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3 px-2">Trending</p>
        {loading ? (
          <p className="text-cream/40 px-2">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="text-cream/40 px-2 text-sm">No posts yet — be the first.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="explore-grid">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/p/${p.id}`}
                data-testid={`explore-post-${p.id}`}
                className="group relative aspect-[9/14] rounded-2xl overflow-hidden bg-white/5"
              >
                {p.media_type === "video" ? (
                  <video src={fileUrl(p.media_path)} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={p.media_path ? fileUrl(p.media_path) : FALLBACKS[i % FALLBACKS.length]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-ink to-transparent">
                  <p className="text-cream text-xs font-bold line-clamp-2">{p.caption}</p>
                  <p className="text-cream/60 text-[10px] mt-1">@{p.author?.username} · ❤︎ {p.likes_count}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
